import type { CitySearchResult, PrimaryDestination } from '@/types/city';

/** First segment of identifying_name for display and auto-suggested titles. */
export function primaryLabelFromDestination(dest: PrimaryDestination): string {
  const raw = dest.identifying_name.trim();
  if (!raw) return '';
  return raw.split(',')[0].trim();
}

/** city_identifying_names array for create payloads (city raw or primary_destination). */
export function buildCityIdentifyingNames(
  destination: PrimaryDestination | null,
  cityRaw: CitySearchResult | null,
): string[] {
  if (cityRaw) {
    const name =
      cityRaw.city_identifying_name ||
      cityRaw.city ||
      cityRaw.name ||
      '';
    return name ? [name] : [];
  }
  if (destination?.identifying_name) {
    return [destination.identifying_name];
  }
  return [];
}

export interface ItineraryUserDefinedDestinationPayload {
  city: string;
  city_identifying_name: string;
  google_place_id?: string;
}

/** itinerary_data.user_defined_destinations for create-trip modal. */
export function buildItineraryUserDefinedDestinations(
  destination: PrimaryDestination | null,
  cityRaw: CitySearchResult | null,
): ItineraryUserDefinedDestinationPayload[] {
  if (cityRaw) {
    return [
      {
        city: cityRaw.city || cityRaw.name || '',
        city_identifying_name:
          cityRaw.city_identifying_name ||
          cityRaw.city ||
          cityRaw.name ||
          '',
        ...(cityRaw.google_place_id ? { google_place_id: cityRaw.google_place_id } : {}),
      },
    ];
  }
  if (destination) {
    const label = primaryLabelFromDestination(destination);
    return [
      {
        city: label,
        city_identifying_name: destination.identifying_name,
        ...(destination.google_place_id ? { google_place_id: destination.google_place_id } : {}),
      },
    ];
  }
  return [];
}

/** Legacy list `city` object for create-list modal when destination is selected. */
export function buildListCityPayload(
  destination: PrimaryDestination | null,
  cityRaw: CitySearchResult | null,
): Record<string, unknown> | null {
  if (cityRaw) {
    return {
      _id: cityRaw._id,
      city: cityRaw.city || cityRaw.name || cityRaw.label || '',
      city_identifying_name:
        cityRaw.city_identifying_name ||
        cityRaw.city ||
        cityRaw.name ||
        '',
      ...(cityRaw.google_place_id ? { google_place_id: cityRaw.google_place_id } : {}),
    };
  }
  if (destination) {
    return {
      city: primaryLabelFromDestination(destination),
      city_identifying_name: destination.identifying_name,
      ...(destination.google_place_id ? { google_place_id: destination.google_place_id } : {}),
    };
  }
  return null;
}
